import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, MapPin, Home, TrendingUp, DollarSign, Percent, BedDouble, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Constants for configuration
const DEFAULT_OCCUPANCY = 65;
const DEFAULT_AREA = 'puerto_plata';
const DEFAULT_BEDROOMS = 2;
const DAYS_PER_MONTH = 30;
const MIN_OCCUPANCY = 30;
const MAX_OCCUPANCY = 95;

// Season durations (months)
const HIGH_SEASON_MONTHS = 4;    // Dec-Mar
const REGULAR_SEASON_MONTHS = 5; // Apr-Aug
const LOW_SEASON_MONTHS = 3;     // Sep-Nov

const RevenueCalculator = () => {
  const { t } = useLanguage();
  const [area, setArea] = useState(DEFAULT_AREA);
  const [propertyType, setPropertyType] = useState('apartment');
  const [bedrooms, setBedrooms] = useState(DEFAULT_BEDROOMS);
  const [occupancy, setOccupancy] = useState(null); // Start as null, will be set from API
  const [marketData, setMarketData] = useState(null);
  const [dataSource, setDataSource] = useState('estimates');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch market data from backend (only once on mount)
  const fetchMarketData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/market-data`);
      if (response.ok) {
        const result = await response.json();
        setMarketData(result.data);
        setDataSource(result.source);
        setLastUpdated(result.updated_at);
        
        // Set initial occupancy from market data for the default area
        if (result.data && result.data[DEFAULT_AREA] && result.data[DEFAULT_AREA].occupancy) {
          setOccupancy(Math.round(result.data[DEFAULT_AREA].occupancy));
        } else {
          setOccupancy(DEFAULT_OCCUPANCY);
        }
      } else {
        setOccupancy(DEFAULT_OCCUPANCY);
      }
    } catch (error) {
      setOccupancy(DEFAULT_OCCUPANCY);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch market data on component mount
  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  // Auto-adjust occupancy when area changes based on AirROI data
  useEffect(() => {
    if (marketData && marketData[area] && marketData[area].occupancy) {
      setOccupancy(Math.round(marketData[area].occupancy));
    }
  }, [area, marketData]);

  // Base rate data - property type multipliers (used with market ADR)
  const rateData = {
    puerto_plata: {
      name: 'Puerto Plata',
      flag: '🏖️',
      rates: {
        studio: { multiplier: 0.6, perBedroom: 0 },
        apartment: { multiplier: 0.8, perBedroom: 20 },
        condo: { multiplier: 1.0, perBedroom: 25 },
        villa: { multiplier: 1.6, perBedroom: 40 },
        house: { multiplier: 1.2, perBedroom: 30 }
      },
      seasonalMultiplier: { high: 1.4, low: 0.8 },
      description: 'Growing tourist destination with great beach access',
      defaultADR: 95
    },
    punta_cana: {
      name: 'Punta Cana',
      flag: '🌴',
      rates: {
        studio: { multiplier: 0.55, perBedroom: 0 },
        apartment: { multiplier: 0.75, perBedroom: 30 },
        condo: { multiplier: 1.0, perBedroom: 40 },
        villa: { multiplier: 1.8, perBedroom: 60 },
        house: { multiplier: 1.3, perBedroom: 45 }
      },
      seasonalMultiplier: { high: 1.5, low: 0.75 },
      description: 'Premium tourist destination with highest demand',
      defaultADR: 150
    },
    las_terrenas: {
      name: 'Las Terrenas',
      flag: '🏝️',
      rates: {
        studio: { multiplier: 0.55, perBedroom: 0 },
        apartment: { multiplier: 0.75, perBedroom: 22 },
        condo: { multiplier: 1.0, perBedroom: 28 },
        villa: { multiplier: 1.6, perBedroom: 45 },
        house: { multiplier: 1.15, perBedroom: 32 }
      },
      seasonalMultiplier: { high: 1.35, low: 0.8 },
      description: 'Popular with European tourists, laid-back vibe',
      defaultADR: 110
    },
    sosua_cabarete: {
      name: 'Sosua/Cabarete',
      flag: '🏄',
      rates: {
        studio: { multiplier: 0.55, perBedroom: 0 },
        apartment: { multiplier: 0.8, perBedroom: 20 },
        condo: { multiplier: 1.0, perBedroom: 28 },
        villa: { multiplier: 1.55, perBedroom: 40 },
        house: { multiplier: 1.15, perBedroom: 30 }
      },
      seasonalMultiplier: { high: 1.35, low: 0.8 },
      description: 'Kitesurfing & beach town, popular with expats',
      defaultADR: 120
    },
    santo_domingo: {
      name: 'Santo Domingo',
      flag: '🏛️',
      rates: {
        studio: { multiplier: 0.55, perBedroom: 0 },
        apartment: { multiplier: 0.8, perBedroom: 18 },
        condo: { multiplier: 1.0, perBedroom: 22 },
        villa: { multiplier: 1.5, perBedroom: 35 },
        house: { multiplier: 1.15, perBedroom: 25 }
      },
      seasonalMultiplier: { high: 1.2, low: 0.9 },
      description: 'Capital city, business & cultural tourism',
      defaultADR: 75
    }
  };

  const propertyTypes = [
    { id: 'studio', name: t('revenue.propertyTypes.studio') || 'Studio', icon: '🏠' },
    { id: 'apartment', name: t('revenue.propertyTypes.apartment') || 'Apartment', icon: '🏢' },
    { id: 'condo', name: t('revenue.propertyTypes.condo') || 'Condo', icon: '🏨' },
    { id: 'villa', name: t('revenue.propertyTypes.villa') || 'Villa', icon: '🏡' },
    { id: 'house', name: t('revenue.propertyTypes.house') || 'House', icon: '🏘️' }
  ];

  const calculateRevenue = () => {
    const areaData = rateData[area];
    const typeData = areaData.rates[propertyType];
    
    // Get base ADR from market data or use default
    const baseADR = marketData && marketData[area] 
      ? marketData[area].avg_daily_rate 
      : areaData.defaultADR;
    
    // Get occupancy from market data or use current slider value
    const currentOccupancy = occupancy !== null ? occupancy : DEFAULT_OCCUPANCY;
    
    // Calculate nightly rate based on property type and bedrooms
    const bedroomCount = propertyType === 'studio' ? 0 : bedrooms;
    const nightlyRate = (baseADR * typeData.multiplier) + (typeData.perBedroom * bedroomCount);
    
    // Seasonal rates
    const highSeasonRate = nightlyRate * areaData.seasonalMultiplier.high;
    const lowSeasonRate = nightlyRate * areaData.seasonalMultiplier.low;
    const regularSeasonRate = nightlyRate;
    
    // Occupied nights per month
    const occupiedNightsPerMonth = (DAYS_PER_MONTH * currentOccupancy) / 100;
    
    // Calculate revenue for each season
    const highSeasonRevenue = highSeasonRate * occupiedNightsPerMonth * HIGH_SEASON_MONTHS;
    const regularSeasonRevenue = regularSeasonRate * occupiedNightsPerMonth * REGULAR_SEASON_MONTHS;
    const lowSeasonRevenue = lowSeasonRate * occupiedNightsPerMonth * LOW_SEASON_MONTHS;
    
    // Total yearly revenue (sum of all seasons)
    const yearlyRevenue = highSeasonRevenue + regularSeasonRevenue + lowSeasonRevenue;
    
    // Monthly average (yearly / 12)
    const monthlyRevenue = yearlyRevenue / 12;
    
    return {
      nightlyRate,
      monthlyRevenue,
      yearlyRevenue,
      occupiedNights: occupiedNightsPerMonth,
      // Seasonal rates
      highSeasonRate,
      regularSeasonRate,
      lowSeasonRate,
      // Seasonal revenue totals
      highSeasonRevenue,
      regularSeasonRevenue,
      lowSeasonRevenue,
      highSeasonMonths: HIGH_SEASON_MONTHS,
      regularSeasonMonths: REGULAR_SEASON_MONTHS,
      lowSeasonMonths: LOW_SEASON_MONTHS,
      // Market data
      marketOccupancy: marketData && marketData[area] ? marketData[area].occupancy : null,
      marketADR: marketData && marketData[area] ? marketData[area].avg_daily_rate : null
    };
  };

  const results = calculateRevenue();
  const selectedArea = rateData[area];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section id="revenue-calculator" className="py-20 bg-gradient-to-br from-[#8B5CF6]/10 via-white to-[#FF6B35]/10">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="inline-block text-white bg-[#8B5CF6] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('revenue.badge') || 'Investment Tool'}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#000080] mb-4">
            {t('revenue.title') || 'Airbnb Revenue Calculator'}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('revenue.subtitle') || 'Estimate your potential rental income in the Dominican Republic'}
          </p>
          {/* Data source indicator */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {dataSource === 'airroi' ? (
              <span className="inline-flex items-center gap-1 text-xs bg-[#22C55E]/20 text-[#22C55E] px-3 py-1 rounded-full">
                <span className="w-2 h-2 bg-[#22C55E] rounded-full animate-pulse"></span>
                Live Market Data from AirROI
                {lastUpdated && <span className="text-[#22C55E]/70 ml-1">• Updated {new Date(lastUpdated).toLocaleDateString()}</span>}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs bg-gray-200 text-gray-600 px-3 py-1 rounded-full">
                Based on Market Estimates
              </span>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator inputs */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
            <h3 className="text-xl font-bold text-[#000080] mb-6 flex items-center gap-2">
              <Calculator className="w-6 h-6 text-[#8B5CF6]" />
              {t('revenue.propertyDetails') || 'Property Details'}
            </h3>

            {/* Area Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-3">
                <MapPin className="w-4 h-4 inline mr-1" />
                {t('revenue.selectArea') || 'Select Area'}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(rateData).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => setArea(key)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      area === key
                        ? 'bg-[#8B5CF6] text-white shadow-lg scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg mr-1">{data.flag}</span>
                    {data.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">{selectedArea.description}</p>
            </div>

            {/* Property Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-3">
                <Home className="w-4 h-4 inline mr-1" />
                {t('revenue.propertyType') || 'Property Type'}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {propertyTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setPropertyType(type.id)}
                    className={`p-3 rounded-xl text-xs font-medium transition-all ${
                      propertyType === type.id
                        ? 'bg-[#FF6B35] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg block mb-1">{type.icon}</span>
                    {type.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Bedrooms */}
            {propertyType !== 'studio' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-3">
                  <BedDouble className="w-4 h-4 inline mr-1" />
                  {t('revenue.bedrooms') || 'Number of Bedrooms'}
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      onClick={() => setBedrooms(num)}
                      className={`w-12 h-12 rounded-xl font-bold transition-all ${
                        bedrooms === num
                          ? 'bg-[#22C55E] text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Occupancy Rate */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-3">
                <Percent className="w-4 h-4 inline mr-1" />
                {t('revenue.occupancy') || 'Expected Occupancy Rate'}: <span className="text-[#8B5CF6] font-bold">{occupancy !== null ? occupancy : '--'}%</span>
                {results.marketOccupancy && (
                  <span className="ml-2 text-xs text-[#22C55E] font-normal">
                    (Market avg: {Math.round(results.marketOccupancy)}%)
                  </span>
                )}
              </label>
              <input
                type="range"
                min={MIN_OCCUPANCY}
                max={MAX_OCCUPANCY}
                value={occupancy !== null ? occupancy : DEFAULT_OCCUPANCY}
                onChange={(e) => setOccupancy(parseInt(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#8B5CF6]"
                disabled={occupancy === null}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{MIN_OCCUPANCY}% (Low)</span>
                <span>{DEFAULT_OCCUPANCY}% (Average)</span>
                <span>{MAX_OCCUPANCY}% (High)</span>
              </div>
            </div>

            {/* Occupancy tips */}
            <div className="bg-[#FFFF00]/20 rounded-xl p-4 text-sm">
              <p className="text-[#000080] font-medium mb-1">💡 {t('revenue.occupancyTip') || 'Occupancy Tips'}:</p>
              <ul className="text-gray-600 text-xs space-y-1">
                <li>• {t('revenue.tip1') || 'New listings: 40-50% in first year'}</li>
                <li>• {t('revenue.tip2') || 'Established listings: 60-75%'}</li>
                <li>• {t('revenue.tip3') || 'SuperHost status can boost by 10-15%'}</li>
              </ul>
            </div>
          </div>

          {/* Results */}
          <div className="bg-gradient-to-br from-[#000080] to-[#000050] rounded-3xl shadow-xl p-8 text-white">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#FFFF00]" />
              {t('revenue.estimatedRevenue') || 'Estimated Revenue'}
            </h3>

            {/* Main revenue display */}
            <div className="bg-white/10 rounded-2xl p-6 mb-6">
              <div className="text-center mb-4">
                <p className="text-white/70 text-sm mb-1">{t('revenue.yearlyRevenue') || 'Estimated Yearly Revenue'}</p>
                <p className="text-5xl font-bold text-[#FFFF00]">
                  {formatCurrency(results.yearlyRevenue)}
                </p>
                <p className="text-white/50 text-xs mt-1">{t('revenue.perYear') || 'per year'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <p className="text-white/60 text-xs">{t('revenue.monthlyAvg') || 'Monthly Average'}</p>
                  <p className="text-2xl font-bold text-[#22C55E]">
                    {formatCurrency(results.monthlyRevenue)}
                  </p>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-xl">
                  <p className="text-white/60 text-xs">{t('revenue.nightlyRate') || 'Nightly Rate'}</p>
                  <p className="text-2xl font-bold text-[#FF6B35]">
                    {formatCurrency(results.nightlyRate)}
                  </p>
                </div>
              </div>
            </div>

            {/* Seasonal breakdown */}
            <div className="space-y-3 mb-6">
              <h4 className="font-semibold text-sm text-white/80">Seasonal Revenue Breakdown:</h4>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white/70 text-sm">🌞 {t('revenue.highSeason') || 'High Season'} (Dec-Mar)</span>
                  <span className="font-bold text-[#FFFF00]">{formatCurrency(results.highSeasonRevenue)}</span>
                </div>
                <div className="flex justify-between items-center text-xs text-white/50">
                  <span>{results.highSeasonMonths} months × {formatCurrency(results.highSeasonRate)}/night</span>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white/70 text-sm">☀️ {t('revenue.regularSeason') || 'Regular Season'} (Apr-Aug)</span>
                  <span className="font-bold text-white">{formatCurrency(results.regularSeasonRevenue)}</span>
                </div>
                <div className="flex justify-between items-center text-xs text-white/50">
                  <span>{results.regularSeasonMonths} months × {formatCurrency(results.regularSeasonRate)}/night</span>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white/70 text-sm">🌧️ {t('revenue.lowSeason') || 'Low Season'} (Sep-Nov)</span>
                  <span className="font-bold text-white/60">{formatCurrency(results.lowSeasonRevenue)}</span>
                </div>
                <div className="flex justify-between items-center text-xs text-white/50">
                  <span>{results.lowSeasonMonths} months × {formatCurrency(results.lowSeasonRate)}/night</span>
                </div>
              </div>
            </div>

            {/* Occupied nights */}
            <div className="bg-[#FFFF00]/20 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-sm">{t('revenue.occupiedNights') || 'Occupied nights/month'}:</span>
                <span className="font-bold text-[#FFFF00]">{results.occupiedNights.toFixed(0)} {t('revenue.nights') || 'nights'}</span>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-white/60 text-sm mb-4">
                {t('revenue.ctaText') || 'Let us help you maximize your rental income'}
              </p>
              <Button
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#FFFF00] hover:bg-[#FFD700] text-[#000080] font-bold px-8 py-3"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                {t('revenue.getStarted') || 'Get Property Management Quote'}
              </Button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          {t('revenue.disclaimer') || 'Estimates are based on market averages and may vary. Actual revenue depends on property condition, amenities, reviews, and market conditions.'}
        </p>
      </div>
    </section>
  );
};

export default RevenueCalculator;
